import React, { useMemo } from 'react';
import { definePage, defineResource, ListQuery } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import MemberRoleForm from 'src/components/Organization/MemberRoleForm';
import { filterInMemory } from 'src/crud/filterInMemory';
import { INVITATION_STATUS_TAGS, ORG_ROLE_TAGS, tagOptions } from 'src/features/orgRoles';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { OrgInvitationRow, cancelOrgInvitation, fetchInvitations } from 'src/hooks/useInvitations';
import { useMemberships } from 'src/hooks/useOrganizations';
import { QUERIES } from 'src/utils/react-query';

/**
 * Like Members, this list lives in the auth service and is reached through the plain
 * functions beneath the hooks. An invitation is created and revoked but never edited, so
 * this page runs with canCreate and canUpdate false — inviting happens from Members, where
 * the person doing it is already looking at who is in the organization.
 */
export const useOrgInvitationsPage = () => {
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships } = useMemberships();
  const active = useMemo(
    () => memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0],
    [memberships, activeOrganizationId]
  );
  const organizationId = active?.id ?? null;

  const resource = useMemo(
    () =>
      defineResource({
        key: QUERIES.ORG_INVITATIONS,
        // better-auth returns every pending invitation in one response; see useOrgMembersPage.
        pageSize: 1000,
        // No CSV export: better-auth returns at most 100 invitations, so an export would truncate
        // silently and look complete.
        exportable: false,
        api: {
          list: async (_scope, query?: ListQuery) => {
            const data = organizationId ? await fetchInvitations(organizationId) : [];
            // better-auth takes no filter; the Status and Role chips apply over the whole list.
            const rows = filterInMemory(data, query?.filter);
            return { data: rows, total: rows.length };
          },
          // Both unreachable: canCreate and canUpdate are false on this page. They exist
          // because the resource type requires all four slots, and they throw rather than
          // resolve so a future page that turns a gate on cannot silently no-op.
          create: async () => {
            throw new Error('Invitations are created from the Members page.');
          },
          update: async () => {
            throw new Error('An invitation cannot be edited; cancel and re-send it.');
          },
          remove: (_scope, id: string) => cancelOrgInvitation(id),
        },
        search: 'client',
        searchFields: ['email'],
        filterable: [
          {
            field: 'status',
            labelKey: 'common.status',
            type: 'enum',
            options: tagOptions(INVITATION_STATUS_TAGS),
          },
          {
            field: 'role',
            labelKey: 'common.role',
            type: 'enum',
            options: tagOptions(ORG_ROLE_TAGS),
          },
        ],
      }),
    [organizationId]
  );

  const page = useMemo(
    () =>
      definePage(resource, {
        titleKey: 'orgSettings.invitations.title',
        // See orgMembers: rendered as `<total> <description>`.
        descriptionKey: 'orgSettings.invitations.title',
        createLabelKey: 'orgSettings.members.invite',
        updateLabelKey: 'orgSettings.members.changeRole',
        deleteHeadingKey: 'orgSettings.invitations.cancelTitle',
        columns: [
          { key: 'email', headerKey: 'common.email', alwaysVisible: true, sortable: true },
          { key: 'role', headerKey: 'common.role', display: { kind: 'tag', map: ORG_ROLE_TAGS } },
          { key: 'status', headerKey: 'common.status', display: { kind: 'tag', map: INVITATION_STATUS_TAGS } },
          {
            key: 'expiresAt',
            headerKey: 'orgSettings.invitations.expiresAt',
            // A dash rather than an empty cell for a missing date, matching the table this
            // replaces — `formatDate` renders null as '', which reads as a layout bug.
            render: (row: OrgInvitationRow) =>
              row.expiresAt ? new Date(row.expiresAt).toLocaleDateString('en-GB') : '-',
          },
        ],
        // Unreachable while canCreate and canUpdate are false; the type requires a component.
        form: MemberRoleForm,
        emptyState: <EmptyState />,
        toPayload: (values: any) => values,
      }),
    [resource]
  );

  return { page, organizationId };
};
